import { useCallback, useRef } from 'react';

const SWIPE_THRESHOLD = 60;

/**
 * Pointer-based swipe-right to reply (WhatsApp-style).
 */
export default function useSwipeToReply(onReply) {
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const draggingRef = useRef(false);
  const offsetRef = useRef(0);
  const elRef = useRef(null);

  const setOffset = useCallback((px) => {
    offsetRef.current = px;
    if (elRef.current) {
      elRef.current.style.transform = px > 0 ? `translateX(${px}px)` : '';
      elRef.current.style.transition = draggingRef.current ? 'none' : 'transform 0.2s ease-out';
    }
  }, []);

  const reset = useCallback(() => {
    draggingRef.current = false;
    setOffset(0);
  }, [setOffset]);

  const onPointerDown = useCallback((e) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    startXRef.current = e.clientX;
    startYRef.current = e.clientY;
    draggingRef.current = true;
    elRef.current = e.currentTarget;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - startXRef.current;
    const dy = Math.abs(e.clientY - startYRef.current);
    if (dy > 30 && dx < 20) {
      reset();
      return;
    }
    if (dx > 0) {
      setOffset(Math.min(dx, 80));
    }
  }, [reset, setOffset]);

  const onPointerUp = useCallback((e, message) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - startXRef.current;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    reset();
    if (dx >= SWIPE_THRESHOLD && onReply) {
      onReply(message);
    }
  }, [onReply, reset]);

  const onPointerCancel = useCallback(() => {
    reset();
  }, [reset]);

  return {
    swipeHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
    },
    bindSwipeEnd: onPointerUp,
  };
}
